import type {
  ApiChatServerChannel,
  ApiChatServerChannelsResponse,
  ApiChatServerMessage,
  ApiChatServerPresign,
  ApiChatServerSettings,
  ApiChatServerUnread,
} from './rest.types'

/** `scope` of POST /users/:id/mark_all_read — which participations to mark. */
export type ChatServerMarkAllReadScope = 'all' | 'archived' | 'non_archived'

export type ChatServerRestClientOptions = {
  /** chat_server REST base url (direct CORS), e.g. `https://chat.kidsout.ru`. */
  getBaseUrl: () => string | null
  /** Bearer for the `Authorization` header — the session access JWT (`aud ∋ kidsout.chat`). */
  getToken: () => Promise<string>
  /**
   * Rotate the token after a 401 — the access jwt can outlive its exp client-side
   * while the session is still alive (APP-4GN), so the client replays the call once
   * with the returned token. Null = genuine auth loss, the 401 stands.
   */
  refreshToken?: () => Promise<string | null>
  /**
   * Current UI locale, sent as `Accept-Language`. chat_server localizes error
   * `message` by it, falling back to the stored `users.locale` and then `ru` — so
   * without the header a viewer reading the site in English gets Russian errors.
   */
  getLocale?: () => string | null
  /** Injectable for tests and non-browser runtimes. */
  fetch?: typeof fetch
}

export class ChatServerRestError extends Error {
  readonly status: number
  /** `errors.detail` — a machine code (`parent_cannot_pay`, …). Never show it to a user. */
  readonly detail: string | null
  /**
   * `errors.message` — the server's own localized copy for this refusal, when it
   * sends one. Named apart from `Error.message`, which stays the technical string.
   */
  readonly userMessage: string | null

  static create(message: string, response: Response, body: ChatServerErrorBody = {}): ChatServerRestError {
    return new ChatServerRestError(message, { status: response.status, ...body })
  }

  constructor(message: string, options: ErrorOptions & { status: number } & ChatServerErrorBody) {
    super(message, options)
    this.name = 'ChatServerRestError'
    this.status = options.status
    this.detail = options.detail ?? null
    this.userMessage = options.userMessage ?? null
  }

  /** A 4xx says the request itself is wrong (forbidden channel, gone, no session), so repeating it verbatim cannot help — except 429, which asks for exactly that. */
  get isPermanent() {
    return this.status >= 400 && this.status < 500 && this.status !== 429
  }
}

/**
 * 429 — the peer asked us to slow down. chat_server has no rate limiter of its
 * own, so today this can only come from something in front of it; the class
 * exists so a limiter anywhere on the path is honoured rather than hammered.
 */
export class ChatServerRateLimitError extends ChatServerRestError {
  /** ISO timestamp of the moment a retry is allowed — from `Retry-After`, or a default cooldown out when the peer named none. */
  readonly retryTimestamp: string

  static create(message: string, response: Response, body: ChatServerErrorBody = {}): ChatServerRateLimitError {
    return new ChatServerRateLimitError(message, {
      status: response.status,
      ...body,
      retryTimestamp: parseRetryAfter(response.headers.get('Retry-After')),
    })
  }

  constructor(message: string, options: ErrorOptions & { status: number; retryTimestamp?: string } & ChatServerErrorBody) {
    super(message, options)
    this.name = 'ChatServerRateLimitError'
    this.retryTimestamp = options.retryTimestamp ?? new Date(Date.now() + DEFAULT_RETRY_AFTER_SECONDS * 1000).toISOString()
  }
}

export class ChatServerRestClient {
  private readonly options: ChatServerRestClientOptions

  constructor(options: ChatServerRestClientOptions) {
    this.options = options
  }

  /** List a user's channels — non-archived by default, `archived: true` for the archive (mutually exclusive server-side). */
  async getUserChannels(
    externalId: string,
    opts?: { archived?: boolean; favorite?: boolean; unread?: boolean; limit?: number; cursor?: string }
  ): Promise<ApiChatServerChannelsResponse> {
    return await this.request<ApiChatServerChannelsResponse>('GET', `/api/users/${encodeURIComponent(externalId)}/channels`, {
      query: {
        archived: opts?.archived ? 'true' : undefined,
        favorite: opts?.favorite ? 'true' : undefined,
        unread: opts?.unread ? 'true' : undefined,
        limit: opts?.limit,
        cursor: opts?.cursor,
      },
    })
  }

  /**
   * Bulk mark-read for the whole account in one call — `channel_ids` lists only the
   * channels that actually had unread, so it is also the set the caller must zero.
   * The server broadcasts `unread_count` per channel besides.
   */
  async markAllRead(externalId: string, scope?: ChatServerMarkAllReadScope): Promise<{ channelIds: string[]; totalUnread: number }> {
    const { data } = await this.request<{ data: { channel_ids: string[]; total_unread: number } }>(
      'POST',
      `/api/users/${encodeURIComponent(externalId)}/mark_all_read`,
      { query: { scope } }
    )
    return { channelIds: data.channel_ids, totalUnread: data.total_unread }
  }

  async getUserUnread(externalId: string): Promise<ApiChatServerUnread[]> {
    const { data } = await this.request<{ data: ApiChatServerUnread[] }>('GET', `/api/users/${encodeURIComponent(externalId)}/unread`)
    return data
  }

  async getChannel(id: string): Promise<ApiChatServerChannel> {
    const { data } = await this.request<{ data: ApiChatServerChannel }>('GET', `/api/channels/${encodeURIComponent(id)}`)
    return data
  }

  /** Page messages of a channel (`before`/`after` cursors are message ids). */
  async getMessages(args: { channelId: string; before?: string; after?: string; limit?: number }): Promise<ApiChatServerMessage[]> {
    const { channelId, ...query } = args
    const { data } = await this.request<{ data: ApiChatServerMessage[] }>('GET', `/api/channels/${encodeURIComponent(channelId)}/messages`, { query })
    return data
  }

  async postMessage(args: { channelId: string; type?: string; body: string; attributes?: Record<string, unknown> }): Promise<ApiChatServerMessage> {
    const { data } = await this.request<{ data: ApiChatServerMessage }>('POST', `/api/channels/${encodeURIComponent(args.channelId)}/messages`, {
      body: { type: args.type ?? 'text', body: args.body, attributes: args.attributes ?? {} },
    })
    return data
  }

  async postChannel(args: { type: 'direct' | 'group' | 'support'; participants: { external_id: string; role?: string }[] }): Promise<ApiChatServerChannel> {
    const { data } = await this.request<{ data: ApiChatServerChannel }>('POST', '/api/channels', { body: args })
    return data
  }

  async patchMessage(args: { id: string; body?: string; attributes?: Record<string, unknown> }): Promise<ApiChatServerMessage> {
    const { data } = await this.request<{ data: ApiChatServerMessage }>('PATCH', `/api/messages/${encodeURIComponent(args.id)}`, {
      body: { body: args.body, attributes: args.attributes },
    })
    return data
  }

  /** Soft-delete (cancel): sets `cancelled_at` and answers 200 with the cancelled message (not 204). Sender-only, within a 30s window (`422 Cancel timeout expired` after). */
  async deleteMessage(id: string): Promise<ApiChatServerMessage> {
    const { data } = await this.request<{ data: ApiChatServerMessage }>('DELETE', `/api/messages/${encodeURIComponent(id)}`)
    return data
  }

  /**
   * Press an inline-keyboard button. 202 with no state of its own — the click is
   * delivered to the message sender's webhook, which answers by editing the message.
   */
  async postMessageCallback(messageId: string, callbackData: string): Promise<void> {
    await this.request<{ data: { status: string } }>('POST', `/api/messages/${encodeURIComponent(messageId)}/callback`, {
      body: { callback_data: callbackData },
    })
  }

  /** Client-facing chat settings (upload limits, reaction emojis). */
  async getSettings(): Promise<ApiChatServerSettings> {
    const { data } = await this.request<{ data: ApiChatServerSettings }>('GET', '/api/settings')
    return data
  }

  /** Reserve a presigned S3 PUT for one file; the returned `upload_token` is claimed by message create. */
  async postAttachmentsPresign(args: { file_name: string; content_type: string; byte_size: number; kind: string }): Promise<ApiChatServerPresign> {
    const { data } = await this.request<{ data: ApiChatServerPresign }>('POST', '/api/attachments/presign', { body: args })
    return data
  }

  /** PUT the file bytes straight to S3 — no bearer; the presigned URL itself is the authorization. */
  async putPresignedUpload(url: string, headers: Record<string, string>, file: Blob, signal?: AbortSignal): Promise<void> {
    const fetchImpl = this.options.fetch ?? fetch
    const response = await fetchImpl(url, { method: 'PUT', headers, body: file, signal })
    if (!response.ok) throw this.restError(`attachment upload PUT responded ${response.status}`, response)
  }

  /** Fetch `/media/:id` bytes under bearer auth — the stable media URLs reject unauthenticated `<img src>` loads. */
  async getMedia(path: string, signal?: AbortSignal): Promise<Blob> {
    const base = this.options.getBaseUrl()
    if (!base) throw new Error('ChatServerRestClient: base url is not configured')
    const token = await this.options.getToken()
    const fetchImpl = this.options.fetch ?? fetch
    const send = (auth: string) => fetchImpl(base.replace(/\/$/, '') + path, { headers: { Authorization: `Bearer ${auth}` }, signal })
    const response = await this.recovering401(await send(token), send)
    if (!response.ok) throw this.restError(`chat_server GET ${path} responded ${response.status}`, response)
    return await response.blob()
  }

  /** Archive for the caller only (per-participant `archived_at`); returns the updated channel. */
  async archiveChannel(id: string): Promise<ApiChatServerChannel> {
    const { data } = await this.request<{ data: ApiChatServerChannel }>('POST', `/api/channels/${encodeURIComponent(id)}/archive`)
    return data
  }

  async unarchiveChannel(id: string): Promise<ApiChatServerChannel> {
    const { data } = await this.request<{ data: ApiChatServerChannel }>('POST', `/api/channels/${encodeURIComponent(id)}/unarchive`)
    return data
  }

  private async request<T>(method: string, path: string, args?: { query?: Record<string, string | number | undefined>; body?: unknown }): Promise<T> {
    const base = this.options.getBaseUrl()
    if (!base) throw new Error('ChatServerRestClient: base url is not configured')
    const token = await this.options.getToken()

    let url = base.replace(/\/$/, '') + path
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(args?.query ?? {})) {
      if (value === undefined) continue
      query.set(key, String(value))
    }
    const qs = query.toString()
    if (qs) url += `?${qs}`

    const headers: Record<string, string> = { Accept: 'application/json' }
    const locale = this.options.getLocale?.()
    if (locale) headers['Accept-Language'] = locale
    let body: string | undefined
    if (args?.body !== undefined) {
      headers['Content-Type'] = 'application/json'
      body = JSON.stringify(args.body)
    }

    const fetchImpl = this.options.fetch ?? fetch
    const send = (auth: string) => fetchImpl(url, { method, headers: { ...headers, Authorization: `Bearer ${auth}` }, body })
    const response = await this.recovering401(await send(token), send)
    if (!response.ok) {
      const body = await readErrorBody(response)
      const suffix = body.detail ?? body.userMessage
      throw this.restError(`chat_server ${method} ${path} responded ${response.status}${suffix ? `: ${suffix}` : ''}`, response, body)
    }
    if (response.status === 204) return undefined as T
    return (await response.json()) as T
  }

  /** Replay a 401 once with a rotated token (see `refreshToken`); anything else passes through. */
  private async recovering401(response: Response, send: (auth: string) => Promise<Response>): Promise<Response> {
    if (response.status !== 401 || !this.options.refreshToken) return response
    const fresh = await this.options.refreshToken()
    if (fresh === null) return response
    return send(fresh)
  }

  /** Maps a failed response onto the error class that fits it. */
  private restError(message: string, response: Response, body: ChatServerErrorBody = {}): ChatServerRestError {
    if (response.status === 429) return ChatServerRateLimitError.create(message, response, body)
    return ChatServerRestError.create(message, response, body)
  }
}

/** Cooldown assumed when a 429 names none — retrying on the normal interval is what keeps a limit tripped. */
const DEFAULT_RETRY_AFTER_SECONDS = 10

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** The three HTTP-date forms of RFC 9110 §5.6.7: IMF-fixdate, obsolete RFC 850, and asctime. */
const HTTP_DATE_PATTERNS = [
  /^[A-Za-z]{3}, (?<day>\d{2}) (?<month>[A-Za-z]{3}) (?<year>\d{4}) (?<hour>\d{2}):(?<minute>\d{2}):(?<second>\d{2}) GMT$/,
  /^[A-Za-z]{6,9}, (?<day>\d{2})-(?<month>[A-Za-z]{3})-(?<year>\d{2}) (?<hour>\d{2}):(?<minute>\d{2}):(?<second>\d{2}) GMT$/,
  /^[A-Za-z]{3} (?<month>[A-Za-z]{3}) (?<day>[ \d]\d) (?<hour>\d{2}):(?<minute>\d{2}):(?<second>\d{2}) (?<year>\d{4})$/,
]

/**
 * `Retry-After` per RFC 9110 §10.2.3 as an ISO timestamp: the header is either
 * `delta-seconds` (a non-negative integer) or an HTTP-date. A date already in the
 * past is legal and means "now", so callers clamp the wait rather than this
 * rejecting it. Parsed positionally rather than through a date library — the
 * header is machine-readable and always GMT, whichever locale the app runs under.
 */
const parseRetryAfter = (value: string | null): string | undefined => {
  const header = value?.trim()
  if (!header) return undefined
  if (/^\d+$/.test(header)) return new Date(Date.now() + Number(header) * 1000).toISOString()
  const date = parseHttpDate(header)
  return date === null ? undefined : new Date(date).toISOString()
}

const parseHttpDate = (header: string): number | null => {
  for (const pattern of HTTP_DATE_PATTERNS) {
    const groups = pattern.exec(header)?.groups
    if (!groups) continue
    const month = MONTHS.indexOf(groups.month)
    if (month < 0) return null
    const day = Number(groups.day)
    const time = Date.UTC(resolveYear(groups.year), month, day, Number(groups.hour), Number(groups.minute), Number(groups.second))
    // Date.UTC rolls an out-of-range day over into the next month (Nov 31 → Dec 1); such a date is not a valid one.
    return new Date(time).getUTCDate() === day ? time : null
  }
  return null
}

/** RFC 9110 §5.6.7: a two-digit year that would land more than 50 years ahead names the most recent past year ending in those digits. */
const resolveYear = (value: string): number => {
  if (value.length === 4) return Number(value)
  const currentYear = new Date().getUTCFullYear()
  const year = Math.floor(currentYear / 100) * 100 + Number(value)
  return year - currentYear > 50 ? year - 100 : year
}

/**
 * The two halves of a chat_server error body: a machine code (presign 422 reasons,
 * `Authorizers::ChatServerChannel` deny reasons) and the server's localized copy.
 * Either may be absent.
 */
type ChatServerErrorBody = { detail?: string | null; userMessage?: string | null }

/**
 * chat_server answers in two shapes and both carry the copy under `message`:
 * `APIError.detail` nests the code as `errors.detail`, `APIError.code` puts it flat
 * as `error` (auth, guest and ownership paths). A changeset 422 fills `errors` with
 * field keys instead, so neither code lookup matches and both fall through to null.
 */
const readErrorBody = async (response: Response): Promise<ChatServerErrorBody> => {
  try {
    const body = (await response.json()) as { error?: unknown; message?: unknown; errors?: { detail?: unknown; message?: unknown } }
    const detail = body?.errors?.detail ?? body?.error
    const userMessage = body?.errors?.message ?? body?.message
    return {
      detail: typeof detail === 'string' ? detail : null,
      userMessage: typeof userMessage === 'string' ? userMessage : null,
    }
  } catch {
    return {}
  }
}
